import { useMemo } from 'react'

import { FlattenedDocumentPage } from '@/lib/api/client'
import { useListTemplateAttributes } from '@/services/queries/template_attributes'
import { OperationNotesEditor } from './OperationNotesEditor/OperationNotesEditor'
import { OperationLabel } from './OperationLabel'
import DraggableImage from './DraggableImage'
import { DraggableVideo } from './DraggableVideo'
import { PartsTable } from './PartsTable'
import { ToolsTable } from './ToolsTable'
import GenericTable from './GenericTable'
import { useVirtualizedElements } from './DraggableElements/useVirtualizedElements'
import { useDocumentState } from '@/state'

const DraggableElementsMap = {
  note: OperationNotesEditor,
  label: OperationLabel,
  image: DraggableImage,
  view: DraggableImage,
  video: DraggableVideo,
  'parts-table': PartsTable,
  'tools-table': ToolsTable,
  'generic-table': GenericTable,
}

const MemoComponent = ({
  Component,
  template,
  isReadOnly,
  documentPage,
  dataType,
  onNudgeStart,
  onNudgeEnd,
}) => {
  return useMemo(
    () => (
      <Component
        template={template}
        isReadOnly={isReadOnly}
        documentPage={documentPage}
        dataType={dataType}
        onNudgeStart={onNudgeStart}
        onNudgeEnd={onNudgeEnd}
      />
    ),
    [
      template.id,
      template.template_values,
      dataType,
      isReadOnly,
      documentPage.id,
      documentPage.template_values,
    ],
  )
}

type DraggableElementsProps = {
  isReadOnly: boolean
  documentPage: FlattenedDocumentPage
  fullSubPageIds?: string[]
  hiddenSubPageIds?: Set<string>
  annotationsContainerRef: React.RefObject<HTMLDivElement>
  onNudgeStart?: (showGrid: boolean) => void
  onNudgeEnd?: (hideGrid: boolean) => void
}
export const DraggableElements = ({
  isReadOnly,
  documentPage,
  fullSubPageIds: passedFullSubPageIds,
  hiddenSubPageIds: passedHiddenSubPageIds,
  annotationsContainerRef,
  onNudgeStart,
  onNudgeEnd,
}: DraggableElementsProps) => {
  const { data: templateAttributes } = useListTemplateAttributes({
    documentPageId: documentPage.id,
  })

  const selectedElement = useDocumentState((state) => state.selectedElement)
  const bulkSelectedTemplateAttributes = useDocumentState(
    (state) => state.bulkSelectedTemplateAttributes,
  )
  const draggingElementId = useDocumentState((state) => state.draggingElementId)

  // Build set of IDs that must stay mounted (selected, multi-selected, or being dragged)
  const forceMountedIds = useMemo(() => {
    const ids = new Set<string>()

    // Keep selected element mounted
    if (selectedElement?.id) {
      ids.add(selectedElement.id)
    }

    // Keep dragging element mounted
    if (draggingElementId) {
      ids.add(draggingElementId)
    }

    // Keep all multi-selected elements mounted
    bulkSelectedTemplateAttributes.forEach((template) => {
      if (template.id) {
        ids.add(template.id as string)
      }
    })

    return ids
  }, [selectedElement?.id, draggingElementId, bulkSelectedTemplateAttributes])

  const fullSubPageIds = useMemo(() => {
    // Use passed values if available (from AnnotationsContainer), otherwise compute
    if (passedFullSubPageIds) return passedFullSubPageIds

    return (
      (documentPage?.template_values?.subPages as
        | Array<string>
        | undefined) || [documentPage?.id as string]
    )
  }, [passedFullSubPageIds, documentPage])

  const hiddenSubPageIds = useMemo(() => {
    // Use passed values if available (from AnnotationsContainer), otherwise compute
    if (passedHiddenSubPageIds) return passedHiddenSubPageIds

    return new Set<string>(
      (documentPage?.template_values?.hiddenSubPages as string[] | undefined) ??
        [],
    )
  }, [passedHiddenSubPageIds, documentPage?.template_values?.hiddenSubPages])

  const subPageIds = useMemo(
    () => fullSubPageIds.filter((id) => !hiddenSubPageIds.has(id)),
    [fullSubPageIds, hiddenSubPageIds],
  )
  const printSubPageId = useMemo(
    () => documentPage?.template_values?.printSubPageId as string | undefined,
    [documentPage],
  )
  const printFirstSubPageId = useMemo(
    () =>
      documentPage?.template_values?.printFirstSubPageId as string | undefined,
    [documentPage],
  )

  const draggableTemplateAttributes = useMemo(
    () =>
      templateAttributes?.filter(
        (templateAttribute) => templateAttribute.data_type !== 'annotation',
      ),
    [templateAttributes],
  )

  const { visibleElements } = useVirtualizedElements({
    templateAttributes: draggableTemplateAttributes,
    subPageIds,
    fullSubPageIds,
    hiddenSubPageIds,
    annotationsContainerRef,
    forceMountedIds,
    printSubPageId,
    printFirstSubPageId,
    documentPageId: documentPage.id as string,
  })

  return visibleElements?.map((templateAttribute) => {
    const dataType =
      templateAttribute.data_type !== 'table'
        ? templateAttribute.data_type
        : templateAttribute.template_values?.type

    const Component = DraggableElementsMap[dataType] || (() => <></>)

    return (
      <MemoComponent
        Component={Component}
        key={templateAttribute.id}
        template={templateAttribute}
        isReadOnly={isReadOnly}
        documentPage={documentPage}
        dataType={dataType}
        onNudgeStart={onNudgeStart}
        onNudgeEnd={onNudgeEnd}
      />
    )
  })
}
